import { useState, useCallback, useEffect, useRef } from 'react';
import subscriptionService from '../services/SubscriptionService';
import analytics from '../services/analytics';

// While entitlement is UNDETERMINED (503/502 from /v1/me/entitlement) re-check after the server's
// `retry_after_s`; asking sooner cannot change the answer. This fallback covers a network error.
const RECONCILE_FALLBACK_MS = 5000;

export function useSubscription({ checkOnMount = false } = {}) {
  const [products, setProducts] = useState([]);
  // null = unknown/undetermined; true/false = authoritative answer.
  const [hasActiveSubscription, setHasActiveSubscription] = useState(null);
  const [determined, setDetermined] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(checkOnMount);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [error, setError] = useState(null);
  const retryAfterRef = useRef(null);

  // `background` re-checks (the reconciler) must not toggle the foreground
  // spinner or the user would see a flicker while browsing.
  const checkSubscription = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setIsCheckingSubscription(true);
      setError(null);
    }
    try {
      const {
        subscribed,
        determined: isDetermined,
        retryAfterMs,
      } = await subscriptionService.getEntitlement();
      retryAfterRef.current = retryAfterMs;
      setDetermined(isDetermined);
      setHasActiveSubscription(isDetermined ? subscribed : null);
      // Enrich every analytics event with the resolved tier (once authoritative).
      if (isDetermined) {
        analytics.setUserContext({ subscription_tier: subscribed ? 'subscriber' : 'free' });
      }
      return isDetermined ? subscribed : null;
    } catch (err) {
      // getEntitlement is defensive and shouldn't throw, but treat any leak as
      // undetermined (never as "not subscribed").
      console.error('useSubscription: Error checking subscription status:', err);
      setDetermined(false);
      setHasActiveSubscription(null);
      return null;
    } finally {
      if (!background) {
        setIsCheckingSubscription(false);
      }
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    setIsLoadingProducts(true);
    setError(null);
    try {
      const fetchedProducts = await subscriptionService.getOffers();
      setProducts(fetchedProducts);
      return fetchedProducts;
    } catch (err) {
      console.error('useSubscription: Error loading subscription plans:', err);
      setError("We couldn't load subscription plans right now. Please try again.");
      return [];
    } finally {
      setIsLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    if (checkOnMount) {
      checkSubscription();
    }
  }, [checkOnMount, checkSubscription]);

  // Background reconciler: while we still don't have an authoritative answer, re-check after the
  // server's retry_after (and when the tab becomes visible again). Undetermined has no give-up:
  // the server answer self-heals. Once `determined` flips true this stops.
  useEffect(() => {
    // Wait for the first answer: it carries the retry_after the first re-check waits.
    if (!checkOnMount || determined || isCheckingSubscription) {
      return undefined;
    }

    let cancelled = false;
    let timer;
    const schedule = () => {
      timer = setTimeout(async () => {
        await checkSubscription({ background: true });
        if (!cancelled) {
          schedule();
        }
      }, retryAfterRef.current ?? RECONCILE_FALLBACK_MS);
    };
    schedule();

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !cancelled) {
        checkSubscription({ background: true });
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkOnMount, determined, isCheckingSubscription, checkSubscription]);

  return {
    products,
    hasActiveSubscription,
    determined,
    isCheckingSubscription,
    isLoadingProducts,
    error,
    checkSubscription,
    fetchProducts,
    clearError: () => setError(null),
  };
}
